import React from "react";
import LegalLayout, { LegalSection } from "@/pages/LegalLayout";

export default function LegalTermsPage() {
  return (
    <LegalLayout
      title="Terms of Service"
      lastUpdated="February 1, 2026"
      testid="legal-terms-page"
    >
      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and
        use of the onebrain shared AI chat workspace product (the
        &ldquo;Service&rdquo;), operated by{" "}
        <strong>Sonrie LLC</strong> (&ldquo;onebrain,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By creating an account,
        joining a workspace, or sending a message through the Service, you
        agree to be bound by these Terms.
      </p>

      <LegalSection
        heading="1. The Service"
        testid="legal-terms-section-service"
      >
        <p>
          onebrain is a multi-user chat workspace that lets two or more humans
          share a single conversation with an AI assistant powered by
          Anthropic&rsquo;s Claude model family. Workspace members can post
          messages to one another and to the assistant, with all messages
          attributed to a named sender.
        </p>
      </LegalSection>

      <LegalSection
        heading="2. Account Eligibility & Security"
        testid="legal-terms-section-eligibility"
      >
        <p>
          You must be at least 18 years old to use the Service. You are
          responsible for keeping your password confidential and for any
          activity that occurs under your account. Notify us at{" "}
          <strong>info@onebrain.team</strong> as soon as you suspect any
          unauthorized access.
        </p>
      </LegalSection>

      <LegalSection
        heading="3. Acceptable Use"
        testid="legal-terms-section-acceptable-use"
      >
        <p>You agree not to use the Service to:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>
            violate any applicable law, regulation, or third-party right;
          </li>
          <li>
            generate, store, or transmit content that is unlawful, infringing,
            defamatory, or harmful to minors;
          </li>
          <li>
            attempt to reverse engineer, scrape, overload, or interfere with
            the Service or its underlying infrastructure;
          </li>
          <li>
            use the assistant to produce content that violates Anthropic&rsquo;s{" "}
            <a
              href="https://www.anthropic.com/legal/aup"
              target="_blank"
              rel="noreferrer"
              className="underline text-[#1E7BFF] hover:text-[#155EC9]"
            >
              Acceptable Use Policy
            </a>
            ; or
          </li>
          <li>
            resell or sublicense access to the Service without our prior
            written consent.
          </li>
        </ul>
      </LegalSection>

      <LegalSection
        heading="4. Billing & Subscriptions"
        testid="legal-terms-section-billing"
      >
        <p>
          Paid plans are billed in advance on a monthly basis through our
          payment processor. Usage above the included allowance is billed at
          the rates shown in your workspace billing settings. All fees are
          non-refundable except where required by law. We may change pricing
          on 30 days&rsquo; notice; existing prepaid periods are honored at
          the previous rate.
        </p>
      </LegalSection>

      <LegalSection
        heading="5. Your Content"
        testid="legal-terms-section-content"
      >
        <p>
          You retain all rights to the messages, files, and other content you
          submit to the Service (&ldquo;Customer Content&rdquo;). You grant
          onebrain a worldwide, non-exclusive license to host, transmit, and
          process Customer Content solely to operate the Service for you and
          your workspace members. You also grant a limited license to
          Anthropic to process Customer Content as needed to generate
          assistant responses on your behalf, subject to the Privacy Policy.
        </p>
      </LegalSection>

      <LegalSection
        heading="6. Assistant Output"
        testid="legal-terms-section-output"
      >
        <p>
          AI-generated responses (&ldquo;Output&rdquo;) may be incomplete,
          inaccurate, or unsuitable for your purpose. You are responsible for
          reviewing Output before relying on it for any consequential decision.
          Output is provided &ldquo;as is&rdquo; with no warranty of fitness
          for a particular purpose. Subject to your compliance with these
          Terms, onebrain assigns to you whatever rights it may have in the
          Output.
        </p>
      </LegalSection>

      <LegalSection
        heading="7. Suspension & Termination"
        testid="legal-terms-section-termination"
      >
        <p>
          We may suspend or terminate your access at any time, with or
          without notice, if we believe you have violated these Terms, if we
          are required to do so by law, or if continued operation would expose
          onebrain or other users to material risk. You may stop using the
          Service at any time by deleting your workspaces or contacting us at{" "}
          <strong>info@onebrain.team</strong>.
        </p>
      </LegalSection>

      <LegalSection
        heading="8. Disclaimers"
        testid="legal-terms-section-disclaimers"
      >
        <p>
          THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS
          AVAILABLE&rdquo;, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
          INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS
          FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        </p>
      </LegalSection>

      <LegalSection
        heading="9. Limitation of Liability"
        testid="legal-terms-section-liability"
      >
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, ONEBRAIN&rsquo;S AGGREGATE
          LIABILITY ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE
          WILL NOT EXCEED THE GREATER OF (A) THE FEES YOU PAID TO ONEBRAIN IN
          THE 12 MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS.
          IN NO EVENT WILL ONEBRAIN BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
          CONSEQUENTIAL, OR PUNITIVE DAMAGES.
        </p>
      </LegalSection>

      <LegalSection
        heading="10. Governing Law"
        testid="legal-terms-section-governing-law"
      >
        <p>
          These Terms are governed by the laws of <strong>Duval County, Florida</strong>,
          without regard to its conflict-of-laws principles. Any dispute will
          be resolved exclusively in the courts located in{" "}
          <strong>Duval County, Florida</strong>.
        </p>
      </LegalSection>

      <LegalSection
        heading="11. Changes to These Terms"
        testid="legal-terms-section-changes"
      >
        <p>
          We may update these Terms from time to time. If we make material
          changes, we will notify you by email or via the Service before the
          changes take effect. Your continued use after the effective date
          constitutes acceptance of the updated Terms.
        </p>
      </LegalSection>

      <LegalSection heading="12. Contact" testid="legal-terms-section-contact">
        <p>
          Questions about these Terms? Reach us at{" "}
          <strong>info@onebrain.team</strong>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
